import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { confirmDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useEffect } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { addPageHeaderAdditional, PageHeader, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { PageTabs } from '@martinus/styles/client/PageTabs.js';
import { html } from 'lit';
import { SgAccountingDetails } from '../../../accounting/client/SgAccountingDetails.js';
import { PersonDashboard } from '../../../client/PersonDashboard.js';
import { SgUserTimeline } from '../../../history/client/UserHistoryList.js';
import { SgMicrosoftUserControl } from '../../../microsoft-graph/client/components/sg-microsoft-user-control.js';
import { hasRoles } from '../../../permissions/api/hasRoles.js';
import { NoPermission } from '../../../permissions/client/NoPermission.js';
import { SGUserRolesMatrix } from '../../../permissions/client/sg-user-roles-matrix.js';
import { type AbacEnabledPage, useAbacEnabledPages } from '../../../permissions/client/useAbacEnabledPages.js';
import { EMPLOYEE_ROLES, STUDENT_ROLES } from '../../../permissions/roles.js';
import type { UserWithPermissionFields } from '../../../permissions/UserWithPermissionFields.js';
import { PersonalQuittungenDownload } from '../../../quittungen/client/PersonalQuittungenDownload.js';
import { SgDonations } from '../../../sepa/client/sg-donations-page.js';
import { UserSepaMandateList } from '../../../sepa/client/UserSepaMandateList.js';
import { UserAPI } from '../../api/UserAPI.js';
import { employeeDetailRouter, SGEmployeeDetails } from '../../employees/client/UserEmployeeDetails.js';
import { UserEmployeeDetailsActions } from '../../employees/UserEmployeeDetailsPolicy.js';
import { Person } from '../../entities/person.js';
import { SgUserProfile } from '../../profile/client/UserDetailsProfile.js';
import { UserProfileActions } from '../../profile/UserProfilePolicy.js';
import { SgSorgeberechtigung } from '../../spender-sorgeberechtigte/sg-sorgeberechtigung.js';
import { SgStudentDetails } from '../../students/sg-student-details.js';
import { UserDetailsActions } from '../UserDetailsPolicy.js';

enum PersonTab {
  PROFIL = 'Profil',
  MITARBEITER = 'Mitarbeiter',
  SCHUELER = 'Schüler',
  IHRE_KINDER = 'Ihre Kinder',
  RECHTEVERWALTUNG = 'Rechteverwaltung',
  BUCHUNGEN = 'Buchungen',
  ABWESENHEITEN = 'Abwesenheiten',
  VERLAUF = 'Verlauf',
  SPENDE = 'Spende',
  SEPA = 'Lastschriftmandate',
  ACCOUNTING = 'Buchhaltung',
  QUITTUNGEN = 'Quittungen',
  MICROSOFT = 'Microsoft',
}

const personSubPages = {
  profil: {
    label: PersonTab.PROFIL,
    path: '/profil',
    permission: UserProfileActions.VIEW_PROFILE,
  },
  mitarbeiter: {
    label: PersonTab.MITARBEITER,
    path: '/mitarbeiter',
    attached: (viewedUser: UserWithPermissionFields) => {
      return hasRoles({ user: viewedUser, roles: EMPLOYEE_ROLES });
    },
    permission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_TAB,
  },
  schueler: {
    label: PersonTab.SCHUELER,
    path: '/schueler',
    attached: (viewedUser: UserWithPermissionFields) => {
      return hasRoles({ user: viewedUser, roles: STUDENT_ROLES });
    },
    permission: UserDetailsActions.VIEW_STUDENT_TAB,
  },
  sorgeberechtigung: {
    label: PersonTab.IHRE_KINDER,
    path: '/kinder',
    attached: (viewedUser: UserWithPermissionFields) => {
      /**
       * Person has atleast one child in their sorgeberechtigteKinderIDs or spendenKinderIDs arrays
       */
      return (viewedUser.sorgeberechtigteKinderIDs?.length || 0) + (viewedUser.spendenKinderIDs?.length || 0) > 0;
    },
    permission: UserDetailsActions.VIEW_SORGBERECHTIGTEN_TAB,
  },
  rechteverwaltung: {
    label: PersonTab.RECHTEVERWALTUNG,
    path: '/rechteverwaltung',
    permission: UserDetailsActions.VIEW_PERMISSION_MATRIX,
  },
  verlauf: {
    label: PersonTab.VERLAUF,
    path: '/verlauf',
    permission: UserDetailsActions.VIEW_TIMELINE,
  },
  spenden: {
    label: PersonTab.SPENDE,
    path: '/spenden',
    permission: UserDetailsActions.VIEW_SEPA_MANDATES,
  },
  accounting: {
    label: PersonTab.ACCOUNTING,
    path: '/accounting',
    permission: UserDetailsActions.VIEW_BUCHALTUNG,
  },
  sepa: {
    label: PersonTab.SEPA,
    path: '/sepa',
    permission: UserDetailsActions.VIEW_SEPA_MANDATES,
  },
  quittungen: {
    label: PersonTab.QUITTUNGEN,
    path: '/quittungen',
    permission: UserDetailsActions.VIEW_SEPA_MANDATES,
  },
  microsoft: {
    label: PersonTab.MICROSOFT,
    path: '/microsoft',
    permission: UserDetailsActions.VIEW_MICROSOFT,
  },
} as const satisfies Record<string, AbacEnabledPage>;

export const personDetailRouter = makeRouter({
  root: {
    path: '/',
    render: params => PersonDashboard({ personID: params.personID }),
    // the person's name from the parent route is the title; '' keeps "root" out of it
    documentTitlePart: '',
  },
  [personSubPages.profil.label]: {
    path: personSubPages.profil.path,
    render: params => SgUserProfile({ personID: params.personID }),
  },
  [personSubPages.mitarbeiter.label]: {
    path: `${personSubPages.mitarbeiter.path}{/*}?`,
    render: params => SGEmployeeDetails({ personID: params.personID }),
    subRouter: employeeDetailRouter,
  },
  [personSubPages.schueler.label]: {
    path: personSubPages.schueler.path,
    render: params => SgStudentDetails({ personID: params.personID }),
  },
  [personSubPages.sorgeberechtigung.label]: {
    path: personSubPages.sorgeberechtigung.path,
    render: params => SgSorgeberechtigung({ personID: params.personID }),
  },
  [personSubPages.rechteverwaltung.label]: {
    path: personSubPages.rechteverwaltung.path,
    render: params => SGUserRolesMatrix({ personID: params.personID }),
  },
  [personSubPages.verlauf.label]: {
    path: personSubPages.verlauf.path,
    render: params => SgUserTimeline({ personID: params.personID }),
  },

  [personSubPages.spenden.label]: {
    path: personSubPages.spenden.path,
    render: params => SgDonations({ personID: params.personID }),
  },
  [personSubPages.quittungen.label]: {
    path: personSubPages.quittungen.path,
    render: params => PersonalQuittungenDownload({ userID: params.personID }),
  },
  [personSubPages.microsoft.label]: {
    path: personSubPages.microsoft.path,
    render: params => SgMicrosoftUserControl({ userID: params.personID }),
  },
  [personSubPages.sepa.label]: {
    path: personSubPages.sepa.path,
    render: params => UserSepaMandateList({ userID: params.personID }),
  },
  [personSubPages.accounting.label]: {
    path: personSubPages.accounting.path,
    render: params => SgAccountingDetails({ referencedEntityID: params.personID ?? '', perspective: 'person' }),
  },
});

// shows the different tabs a user has permission to enter
export const SGUserDetails = stateful((props: { personID: Maybe<string> }) => {
  if (!props.personID) throw new Error('No user given');

  const { value: person } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID,
      )({
        _id: 1,
        roles: { name: 1, contexts: 1 },
        debitorennummer: 1,
        mitarbeiterDaten: { personalNr: 1 },
        profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
        sorgeberechtigteKinderIDs: 1,
        spendenKinderIDs: 1,
      }),
    [props.personID],
  );

  const availablePages = useAbacEnabledPages({
    pages: Object.values(personSubPages),
    viewedUser: person,
  });

  const rightHeaderContent =
    personDetailRouter.currentRoute.value.route.name === personSubPages.mitarbeiter?.label
      ? html`
          <div
            @click=${async () => {
              if (person?.mitarbeiterDaten?.personalNr) return;
              if (
                await confirmDialog('Hier können Sie die Personalnummer manuell erstellen.', {
                  acceptText: 'Personalnummer erstellen',
                  declineText: 'Abbrechen',
                  highlightDecline: true,
                })
              ) {
                await UserAPI.createPersonalNr(person?._id ?? '')();
              }
            }}
          >
            <p
              class="truncate font-semibold text-base-content ${
                !person?.mitarbeiterDaten?.personalNr ? 'cursor-pointer hover:text-primary' : 'cursor-default'
              }"
            >
              Personalnr.: ${person?.mitarbeiterDaten?.personalNr ?? '---'}
            </p>
          </div>
        `
      : '';

  useEffect(() => {
    addPageHeaderAdditional(
      0,
      PageTabs({
        tabs: (availablePages ?? []).map(subPage => ({ key: subPage.label, label: subPage.label })),
        activeKey: () => personDetailRouter.currentRoute.value.route.name,
        onSelect: tabKey => {
          personDetailRouter.goTo(tabKey as (typeof personSubPages)[keyof typeof personSubPages]['label']);
        },
      }),
    );
    return () => {
      removePageHeaderAdditional(0);
    };
  }, [availablePages]);

  // the tabs only offer the allowed pages, but a pasted or reloaded URL reaches the others too, whose requests the
  // server denies
  const currentPage = Object.values(personSubPages).find(
    page => page.label === personDetailRouter.currentRoute.value.route.name,
  );
  const outlet =
    !currentPage || availablePages?.includes(currentPage)
      ? personDetailRouter.outlet.value
      : availablePages
        ? NoPermission({})
        : PageLoader({});

  return html`
    ${PageHeader({
      title: `${person?.profilDaten?.stammdaten?.vorname ?? '---'} ${person?.profilDaten?.stammdaten?.nachname ?? '---'} - ${person?.debitorennummer ?? '---'}`,
      showBackButton: true,
      rightContent: rightHeaderContent,
      onTitleClick: () => {
        goTo(`/personen/${person?._id}`);
      },
    })}
    ${outlet}
  `;
});
